'use client';

import { useCallback, useMemo } from 'react';

import type { Chapter } from '@/features/fanfic-page/types/fanfic';

export interface UseFanficNavigationReturn {
  currentChapterIndex: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
  goToPreviousChapter: () => void;
  goToNextChapter: () => void;
}

export interface UseFanficNavigationProps {
  chapters: Chapter[];
  selectedChapterId: string | null;
  setSelectedChapterId: (id: string | null) => void;
}

export function useFanficNavigation({
  chapters,
  selectedChapterId,
  setSelectedChapterId,
}: UseFanficNavigationProps): UseFanficNavigationReturn {
  const currentChapterIndex = useMemo(() => {
    return chapters.findIndex((ch) => ch.id === selectedChapterId);
  }, [chapters, selectedChapterId]);

  // Derived state - no need for useMemo
  const canGoPrevious = currentChapterIndex > 0;
  const canGoNext = currentChapterIndex < chapters.length - 1;

  // Stable navigation functions using functional setState pattern
  const goToPreviousChapter = useCallback(() => {
    if (canGoPrevious) {
      setSelectedChapterId(chapters[currentChapterIndex - 1]?.id);
    }
  }, [canGoPrevious, currentChapterIndex, chapters, setSelectedChapterId]);

  const goToNextChapter = useCallback(() => {
    if (canGoNext) {
      setSelectedChapterId(chapters[currentChapterIndex + 1]?.id);
    }
  }, [canGoNext, currentChapterIndex, chapters, setSelectedChapterId]);

  return {
    currentChapterIndex,
    canGoPrevious,
    canGoNext,
    goToPreviousChapter,
    goToNextChapter,
  };
}
